import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Divider,
  useMediaQuery,
  useTheme,
  Pagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import { authenticatedFetch } from "../../utils/api";
import DiaCircle from "../DiaCircle";
import GaugeChip from "../GaugeChip";
import { formatInteger, truncateText, PROGRAM_NAME_MAX_WIDTH, truncatedTextStyle } from "../../utils/formatters";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface Employee {
  id: number;
  name: string;
  phone: string;
}

interface Company {
  id: number;
  name: string;
}

interface Program {
  id: number;
  lot?: string;
  company: Company;
}

interface EmployeeShiftDetail {
  id: number;
  dia: string;
  gauge: string;
  rolls: number;
  weight: number;
  machine_type: string;
  program_id: number;
  program: Program;
}

interface EmployeeShift {
  id: number;
  shift_date: string;
  shift_time: string;
  shift_time_display: string;
  total_rolls: number;
  total_weight: number;
  specs_display: string;
  dias_display: string;
  gauges_display: string;
  machine_types_display: string;
  employee: Employee;
  employee_shift_details: EmployeeShiftDetail[];
  created_at: string;
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

export default function EmployeeShiftList() {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState<EmployeeShift[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [filterDate, setFilterDate] = useState<string>("");
  const [filterShiftTime, setFilterShiftTime] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { translations: t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, [filterDate, filterShiftTime, page]);

  useEffect(() => {
    fetchMachines();
  }, []);

  const fetchMachines = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/machines`);
      if (response.ok) {
        const data = await response.json();
        setMachines(data);
      }
    } catch (err) {
      console.error("Error fetching machines:", err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterDate) params.append("shift_date", filterDate);
      if (filterShiftTime) params.append("shift_time", filterShiftTime);
      params.append("page", page.toString());
      params.append("per_page", "25");

      const url = `${API_URL}/employee_shifts?${params.toString()}`;
      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch employee shifts");
      }

      const data = await response.json();
      setShifts(data);
      
      // Get pagination info from headers
      const totalPagesHeader = response.headers.get('X-Total-Pages');
      if (totalPagesHeader) {
        setTotalPages(parseInt(totalPagesHeader));
      }
    } catch (err) {
      console.error("Error fetching employee shifts:", err);
      setError("Failed to load employee shifts");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const renderShiftTimeIcon = (shiftTime: string) => {
    if (shiftTime === "day") {
      return <WbSunnyIcon fontSize="small" color="warning" />;
    } else if (shiftTime === "night") {
      return <NightsStayIcon fontSize="small" color="info" />;
    }
    return null;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getUniquePrograms = (shift: EmployeeShift) => {
    const programMap = new Map<number, Program>();
    shift.employee_shift_details.forEach((detail) => {
      if (detail.program && !programMap.has(detail.program.id)) {
        programMap.set(detail.program.id, detail.program);
      }
    });
    return Array.from(programMap.values());
  };

  const getUniqueCompanies = (shift: EmployeeShift) => {
    const companyMap = new Map<number, Company>();
    shift.employee_shift_details.forEach((detail) => {
      if (detail.program?.company && !companyMap.has(detail.program.company.id)) {
        companyMap.set(detail.program.company.id, detail.program.company);
      }
    });
    return Array.from(companyMap.values());
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Box 
          sx={{ 
            mb: { xs: 2, sm: 3 }, 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center' 
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/employee-shifts/create")}
            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: '160px' } }}
          >
            {t.employeeShifts.createNew}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Card sx={{ mb: { xs: 2, sm: 3 } }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t.filter}
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 200 }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{t.employeeShifts.shiftType}</InputLabel>
                <Select
                  value={filterShiftTime}
                  label={t.employeeShifts.shiftType}
                  onChange={(e) => setFilterShiftTime(e.target.value)}
                >
                  <MenuItem value="">{t.filter}</MenuItem>
                  <MenuItem value="day">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WbSunnyIcon fontSize="small" />
                      <span>{t.employeeShifts.day}</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="night">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <NightsStayIcon fontSize="small" />
                      <span>{t.employeeShifts.night}</span>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
              {(filterDate || filterShiftTime) && (
                <Button
                  onClick={() => {
                    setFilterDate("");
                    setFilterShiftTime("");
                  }}
                >
                  {t.clear}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        {shifts.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                {t.employeeShifts.noShifts}
              </Typography>
            </CardContent>
          </Card>
        ) : isMobile ? (
          // Mobile Card View
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {shifts.map((shift) => {
              const uniquePrograms = getUniquePrograms(shift);
              const uniqueCompanies = getUniqueCompanies(shift);
              return (
                <Card 
                  key={shift.id}
                  onClick={() => navigate(`/employee-shifts/${shift.id}`)}
                  sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                >
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6" component="div">
                        {shift.employee.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {renderShiftTimeIcon(shift.shift_time)}
                        <Typography variant="caption" color="text.secondary">
                          {shift.shift_time === "day" ? "Day" : "Night"}
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      {formatDate(shift.shift_date)} • {shift.employee.phone}
                    </Typography>
                    
                    <Divider sx={{ my: 1.5 }} />
                    
                    <Stack spacing={1}>
                      {uniqueCompanies.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Companies:</Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                            {uniqueCompanies.map((company) => (
                              <Chip
                                key={company.id}
                                label={company.name}
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/companies/${company.id}`);
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      {uniquePrograms.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Programs:</Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                            {uniquePrograms.map((program) => {
                              const programText = program.lot || `#${program.id}`;
                              return (
                                <Chip
                                  key={program.id}
                                  label={truncateText(programText, 20)}
                                  size="small"
                                  variant="outlined"
                                  sx={{ maxWidth: PROGRAM_NAME_MAX_WIDTH.chipMobile }}
                                  title={programText}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/programs/${program.id}`);
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Dia:</Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {shift.employee_shift_details
                            .filter((detail, index, self) => 
                              index === self.findIndex((d) => 
                                d.dia === detail.dia && d.gauge === detail.gauge && d.machine_type === detail.machine_type
                              )
                            )
                            .map((detail, idx) => (
                              <DiaCircle
                                key={idx}
                                dia={detail.dia}
                                gauge={detail.gauge}
                                machineType={detail.machine_type}
                                machines={machines}
                                size="small"
                              />
                            ))}
                        </Box>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Gauge:</Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {(shift.gauges_display || shift.specs_display?.split('/')[1]?.replace(/ GG/g, '') || '')
                            .split(", ")
                            .filter(g => g)
                            .map((gauge, idx) => (
                              <GaugeChip
                                key={idx}
                                gauge={gauge}
                              />
                            ))}
                        </Box>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Machine Types:</Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {shift.employee_shift_details
                            .filter((detail, index, self) => 
                              index === self.findIndex((d) => d.machine_type === detail.machine_type)
                            )
                            .map((detail, idx) => (
                              <Chip
                                key={idx}
                                label={detail.machine_type}
                                size="small"
                                sx={{
                                  bgcolor: detail.machine_type === 'rib' ? '#e0e0e0' : '#757575',
                                  color: detail.machine_type === 'rib' ? '#000' : '#fff',
                                }}
                              />
                            ))}
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 0.5 }} />
                      
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={600}>Total Rolls:</Typography>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          {formatInteger(shift.total_rolls || 0)}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={600}>Total Weight:</Typography>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          {shift.total_weight ? Number(shift.total_weight).toFixed(2) : "0.00"} kg
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          // Desktop Table View
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Shift</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Program(s)</TableCell>
                  <TableCell>Company(ies)</TableCell>
                  <TableCell>Dia</TableCell>
                  <TableCell>Gauge (GG)</TableCell>
                  <TableCell align="right">Total Rolls</TableCell>
                  <TableCell align="right">Total Weight (kg)</TableCell>
                  <TableCell>Machine Types</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shifts.map((shift) => (
                    <TableRow 
                      key={shift.id} 
                      hover
                      onClick={() => navigate(`/employee-shifts/${shift.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{formatDate(shift.shift_date)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {renderShiftTimeIcon(shift.shift_time)}
                          <span>{shift.shift_time === "day" ? "Day" : "Night"}</span>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{shift.employee.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {shift.employee.phone}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: '200px' }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {getUniquePrograms(shift).map((program, idx, arr) => {
                            const programText = program.lot || `#${program.id}`;
                            return (
                              <React.Fragment key={program.id}>
                                <Link
                                  component="button"
                                  variant="body2"
                                  sx={{ 
                                    fontSize: "0.875rem", 
                                    cursor: "pointer",
                                    ...truncatedTextStyle(PROGRAM_NAME_MAX_WIDTH.chip),
                                    display: 'inline-block',
                                  }}
                                  title={programText}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/programs/${program.id}`);
                                  }}
                                >
                                  {truncateText(programText, 25)}
                                </Link>
                                {idx < arr.length - 1 && <span>,</span>}
                              </React.Fragment>
                            );
                          })}
                          {getUniquePrograms(shift).length === 0 && "-"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {getUniqueCompanies(shift).map((company, idx, arr) => (
                            <React.Fragment key={company.id}>
                              <Link
                                component="button"
                                variant="body2"
                                sx={{ fontSize: "0.875rem", cursor: "pointer" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/companies/${company.id}`);
                                }}
                              >
                                {company.name}
                              </Link>
                              {idx < arr.length - 1 && <span>,</span>}
                            </React.Fragment>
                          ))}
                          {getUniqueCompanies(shift).length === 0 && "-"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {shift.employee_shift_details
                            .filter((detail, index, self) => 
                              index === self.findIndex((d) => 
                                d.dia === detail.dia && d.gauge === detail.gauge && d.machine_type === detail.machine_type
                              )
                            )
                            .map((detail, idx) => (
                              <DiaCircle
                                key={idx}
                                dia={detail.dia}
                                gauge={detail.gauge}
                                machineType={detail.machine_type}
                                machines={machines}
                                size="small"
                              />
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {(shift.gauges_display || shift.specs_display?.split('/')[1]?.replace(/ GG/g, '') || '')
                            .split(", ")
                            .filter(g => g)
                            .map((gauge, idx) => (
                              <GaugeChip
                                key={idx}
                                gauge={gauge}
                              />
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{formatInteger(shift.total_rolls || 0)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {shift.total_weight ? Number(shift.total_weight).toFixed(2) : "0.00"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {shift.employee_shift_details
                            .filter((detail, index, self) => 
                              index === self.findIndex((d) => d.machine_type === detail.machine_type)
                            )
                            .map((detail, idx) => (
                              <Chip
                                key={idx}
                                label={detail.machine_type}
                                size="small"
                                sx={{
                                  bgcolor: detail.machine_type === 'rib' ? '#e0e0e0' : '#757575',
                                  color: detail.machine_type === 'rib' ? '#000' : '#fff',
                                }}
                              />
                            ))}
                          {shift.employee_shift_details.length === 0 && "-"}
                        </Box>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>
    </Container>
  );
}
